<script setup lang="ts">
const props = defineProps<{
  description: string
  categoryPath: string
  tags: never[]
  criterionValues: never
  canonicalProductId: number
}>()

const {
  description,
  categoryPath,
  tags,
  criterionValues,
  canonicalProductId
} = toRefs(props)

const { t } = useI18n();
const textContainer = ref(null);
const textContent = ref('');
const containerHeight = ref('auto');
const showMore = ref(false);
const charsLimit = ref(500);

const truncatedDescription = computed(() => {
  let truncated = '';
  let charCount = 0;
  let inTag = false;
  let tagBuffer = '';
  const tagStack = [];

  for (let i = 0; i < description.value.length; i++) {
    const char = description.value[i];

    if (char === '<') {
      inTag = true;
      tagBuffer = char;
    } else if (char === '>') {
      inTag = false;
      tagBuffer += char;

      if (tagBuffer.startsWith('</')) {
        // Closing tag
        tagStack.pop();
      } else if (!tagBuffer.startsWith('<!') && !tagBuffer.endsWith('/>')) {
        // Opening tag
        const tagName = tagBuffer.match(/<([a-z]+[1-6]?)/i)[1];
        tagStack.push(tagName);
      }

      truncated += tagBuffer;
      tagBuffer = '';
    } else if (inTag) {
      tagBuffer += char;
    } else {
      truncated += char;
      charCount++;

      if (charCount >= charsLimit.value) {
        truncated += '...';
        break;
      }
    }
  }

  // Close any unclosed tags
  while (tagStack.length) {
    const tagName = tagStack.pop();
    truncated += `</${tagName}>`;
  }

  return truncated;
})

const displayedDescription = computed(() => {
  if (showMore.value) {
    return description.value;
  } else {
    return truncatedDescription.value;
  }
})

const showButton = computed(() => {
  return description.value.length > charsLimit.value;
});

const buttonText = computed(() => {
  return showMore.value ? t('www.see.less') : t('find_out_more');
});

const toggleShowMore = () => {
  showMore.value = !showMore.value;
};

</script>

<template>
  <div>
    <div v-if="criterionValues.length <= 3" class="df fww mt-1">
      <div
          v-for="(criterionValue, index) in criterionValues"
          :key="index"
          class="fb33 pb-1 product-page__cara">
        <p class="weight--intermedium">{{ criterionValue.displayName }}</p>
        <h3>{{ criterionValue.value }}</h3>
      </div>
      <div>
        <p class="weight--intermedium">{{ $t('www.product.ref') }}</p>
        <div>CP{{ canonicalProductId }}</div>
      </div>
    </div>

    <div ref="textContainer"  class="product-page__description description-container" :style="{ height: containerHeight }">
      <div ref="textContent" v-html="displayedDescription" />
    </div>

    <span
        v-if="showButton"
        class="size--body weight--intersbold underline-text pointer"
        data-tclz-selector="see-more-description"
        :data-tclz-label="categoryPath"
        data-tclz-category="product"
        @click="toggleShowMore">
        {{ buttonText }}
    </span>

    <div class="mt-2 mb-1 weight--intermedium ctabs__criterions">{{ $t('www.brand.associated_categories') }}</div>
    <div class="df fww mt-1">
      <a
         v-for="(tag, index) in tags"
         :key="index"
         :href="tag.url"
         class="label--big label--highlight-2 mr-h mb-h"
         data-tclz-selector="tag"
         :data-tclz-label="categoryPath"
         data-tclz-category="product">{{ tag.name }}</a>
    </div>
  </div>
</template>

<style>
.description-container {
  overflow: hidden;
  transition: height 0.3s ease;
}
</style>
